import {
  Button,
  Icon,
  fieldKey,
  useFieldErrorsPublisher,
  type IconName,
} from "@sarva/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { isAnnounced } from "@/shared/ActionErrors";
import { describeProblem, type ProblemView } from "@/shared/api";

/**
 * doc 27 §6, rendered.
 *
 * One component per *row* of that table would give six near-identical panels
 * and six chances for one of them to be forgotten; one component that switches
 * on `ProblemView["kind"]` gives the guarantee the table is asking for — every
 * failure has a designed state, and adding a row means the compiler tells you
 * where it goes.
 *
 * (H) doc 27 §6: "No raw exception text, stack trace or opaque code reaches a
 * user. Every error message says what happened and what to do next." The title
 * comes from the generated catalogue and the body from the problem document's
 * `detail`, which doc 22 §4 (H) requires to be safe to show.
 */

const ICON: Record<ProblemView["kind"], IconName> = {
  unauthenticated: "shield",
  forbidden: "shield",
  "not-found": "search",
  conflict: "refresh",
  validation: "info-circle",
  "rate-limited": "clock",
  server: "octagon",
  unknown: "octagon",
};

export interface ProblemNoticeProps {
  /** The caught value. Anything: it is classified here, never at the call site. */
  error: unknown;
  onRetry?: () => void;
  /** Rendered under the message — a "back to the list" link, usually. */
  children?: ReactNode;
  /** `page` centres it in the layout; `inline` sits it inside a panel. */
  variant?: "page" | "inline";
}

export function ProblemNotice({
  error,
  onRetry,
  children,
  variant = "page",
}: ProblemNoticeProps) {
  // (H) Nothing caught is not an unknown error. `error` is "the caught value", and
  // `undefined` is what a mutation carries before it has failed — so rendering it
  // classified absence as `unknown` and put a permanent "Something went wrong on our
  // side" on any screen that mounted this unconditionally. Every existing call site
  // happened to sit inside an `isError` branch, which is why it had not bitten; the
  // guard belongs here rather than at each of them, because the next call site will
  // make the same assumption this component's own prop name invites.
  const publish = useFieldErrorsPublisher();
  const client = useQueryClient();
  const announcedError = isAnnounced(error);
  // [D239] An announced failure was already raised as a pop-up; what is left for the form is
  // putting each field's message under its input. Cleared when the error goes (the next try).
  useEffect(() => {
    if (publish === null || !announcedError) return undefined;
    const view = describeProblem(error);
    if (view.kind !== "validation" || view.fields.length === 0)
      return undefined;
    const map = new Map<string, string>();
    for (const f of view.fields)
      if (!map.has(fieldKey(f.field))) map.set(fieldKey(f.field), f.detail);
    publish(map);
    return () => publish(new Map());
  }, [publish, announcedError, error]);

  if (error === null || error === undefined) return null;
  if (announcedError) {
    // An open dialog hides the page — the pop-up included — from assistive technology, so a
    // form inside one repeats the failure to a screen reader, and only to one.
    if (publish === null) return null;
    const view = describeProblem(error);
    return (
      <p
        role="alert"
        className="sr-only"
        data-problem-kind={view.kind}
        data-problem-code={view.code}
      >
        {view.title}. {view.detail}
        {view.kind === "conflict" && view.specifics !== undefined
          ? ` ${view.specifics}`
          : ""}
      </p>
    );
  }

  const view = describeProblem(error);
  // [D239] A panel that failed to load offers a retry even when its screen supplied none: it
  // refetches the queries that failed. Offered only when there is one to refetch.
  const failedQueries = client
    .getQueryCache()
    .findAll({ predicate: (q) => q.state.status === "error" }).length;
  const retry =
    onRetry ??
    (failedQueries > 0
      ? () => {
          void client.refetchQueries({
            predicate: (q) => q.state.status === "error",
          });
        }
      : undefined);
  const danger = view.kind === "server" || view.kind === "unknown";

  return (
    <div
      role="alert"
      data-problem-kind={view.kind}
      data-problem-code={view.code}
      className={
        variant === "page"
          ? "flex flex-col items-center gap-2 rounded-lg border border-default bg-default px-6 py-12 text-center"
          : "flex flex-col items-start gap-2 rounded-sm border border-default bg-subtle px-4 py-3"
      }
    >
      <Icon
        name={ICON[view.kind]}
        size={variant === "page" ? 7 : 4}
        className={danger ? "text-danger" : "text-tertiary"}
      />
      <p className="text-base font-semibold text-default">{view.title}</p>
      <p className="max-w-prose text-sm text-secondary">{view.detail}</p>

      {view.kind === "forbidden" && view.missing.length > 0 ? (
        <p className="text-xs text-tertiary">
          Ask an administrator for{" "}
          <span className="identifier">{view.missing.join(", ")}</span>.
        </p>
      ) : null}

      {/*
        The half of a `409` a person can act on. The heading and `detail` are
        the same sentence for every conflict in the product; this one names the
        constraint that refused — "Another patient at this practice already
        holds that medical record number" — and, by the API's own rule, names
        the field rather than echoing the value.
      */}
      {view.kind === "conflict" && view.specifics !== undefined ? (
        <p className="max-w-prose text-sm text-secondary">{view.specifics}</p>
      ) : null}

      {view.kind === "conflict" &&
      view.expectedVersion !== undefined &&
      view.actualVersion !== undefined ? (
        <p className="text-xs text-tertiary">
          You were editing version{" "}
          <span className="identifier tabular-nums">
            {view.expectedVersion}
          </span>
          ; the current version is{" "}
          <span className="identifier tabular-nums">{view.actualVersion}</span>.
        </p>
      ) : null}

      {view.kind === "validation" && view.fields.length > 0 ? (
        <ul className="max-w-prose list-none text-sm text-danger">
          {view.fields.map((field) => (
            <li key={`${field.field}:${field.code}`}>
              <span className="identifier">{field.field}</span> — {field.detail}
            </li>
          ))}
        </ul>
      ) : null}

      {(view.kind === "server" || view.kind === "unknown") &&
      view.requestId !== null ? (
        <p className="text-xs text-tertiary">
          Reference <span className="identifier">{view.requestId}</span>
        </p>
      ) : null}

      {view.kind === "rate-limited" ? (
        <RetryCountdown
          seconds={view.retryAfterSeconds}
          {...(retry ? { onRetry: retry } : {})}
        />
      ) : retry !== undefined &&
        (onRetry !== undefined ||
          (view.kind !== "validation" &&
            view.kind !== "forbidden" &&
            view.kind !== "not-found")) ? (
        <div className="mt-2">
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}

      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

/**
 * doc 27 §6, the `429` row: "backoff with a visible retry timer".
 *
 * Visible is the requirement. A silent backoff looks identical to a hung
 * screen, and the user's response to a hung screen is to click again — which
 * is exactly the traffic the limit exists to stop.
 */
export function RetryCountdown({
  seconds,
  onRetry,
}: {
  seconds: number;
  onRetry?: () => void;
}) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil(seconds)),
  );

  useEffect(() => {
    setRemaining(Math.max(0, Math.ceil(seconds)));
  }, [seconds]);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  return (
    <div className="mt-2 flex flex-col items-center gap-2">
      <p
        className="text-sm text-secondary"
        aria-live="polite"
        data-retry-remaining={remaining}
      >
        {remaining > 0
          ? `Retrying is available in ${String(remaining)}s.`
          : "You can try again now."}
      </p>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry} disabled={remaining > 0}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
